import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  Box,
  Button,
  CardContent,
  Container,
  Divider,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import { styled, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { GoogleLogin } from '@react-oauth/google';
import * as Sentry from "@sentry/react";
import axios from "axios";
import { MuiOtpInput } from "mui-one-time-password-input"; // Add this import
import { usePostHog } from "posthog-js/react";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import PhoneNumberInput from "../Common/PhoneNumberInput";
import { MockBubbles } from "../MockBubbles";
import { PrimaryButton } from "../StyledComponents";
import { googleLogin, loginUser } from "../user/userSlice";
import { AppStoreButton } from "./AppStoreButton";
import CSRFToken from "./CSRFToken";

const COUNTRY_BLOCKED_ERROR_MESSAGE =
  "Signing in with phone number is no longer supported in this country";

// Add these styled components
const StyledBox = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  position: "relative",
  borderTopLeftRadius: 16,
  borderTopRightRadius: 16,
  visibility: "visible",
  right: 0,
  left: 0,
}));

function StaffLogin() {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [verificationUuid, setVerificationUuid] = useState(null);
  const [country, setCountry] = useState("US");
  const [error, setError] = useState("");
  const [step, setStep] = useState(0);
  const [canResend, setCanResend] = useState(false);
  const [resendTimer, setResendTimer] = useState(30);
  const [signupUrl, setSignupUrl] = useState("");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(true);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const lastProcessedValue = useRef("");
  const posthog = usePostHog();
  
  // Check if user is already authenticated and redirect
  const isAuthenticated = useSelector((state) => state.user.isAuthenticated);
  
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    let timer;
    if (step === 1 && !canResend && resendTimer > 0) {
      timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
    } else if (resendTimer === 0) {
      setCanResend(true);
    }
    return () => clearTimeout(timer);
  }, [canResend, resendTimer, step]);

  useEffect(() => {
    if (verificationCode.length === 6) {
      handleVerifyLogin(verificationCode);
    }
  }, [verificationCode]);

  const handleGoogleSuccess = async (credentialResponse) => {
    console.log("Google success callback triggered:", credentialResponse);
    setError("");

    try {
      posthog.capture("staff_google_signin_clicked");

      const result = await dispatch(
        googleLogin(credentialResponse.credential)
      ).unwrap();

      if (result.isAuthenticated) {
        console.log("Authentication successful, redirecting...");
        posthog.capture("staff_google_signin_completed_successfully");
        postLoginRedirect();
      } else {
        const errorMessage = result.error || "Authentication failed. Please try again.";
        console.log("Authentication failed:", errorMessage);
        setError(errorMessage);
      }
    } catch (error) {
      console.error("Google Sign-In error:", error);
      
      if (error?.error) {
        setError(error.error);
      } else if (error?.status === 403) {
        setError("You don't have permission to access this resource");
      } else if (error?.status === 400) {
        setError("Invalid Google sign-in credentials. Please try again.");
      } else {
        setError("Google sign-in failed. Please try again.");
      }
      Sentry.captureException(error);
      posthog.capture("staff_google_signin_error", {
        error: error?.error || error.message,
        status: error?.status,
      });
    }
  };

  const handleGoogleError = (error) => {
    console.log("Google error callback triggered:", error);
    setError("Google sign-in was cancelled or failed. Please try again.");
    posthog.capture("staff_google_signin_error");
  };

  const handleInitiateLogin = async (e) => {
    e.preventDefault();
    setError("");
    setSignupUrl("");

    try {
      posthog.capture("login_continue_clicked", {
        country: country,
        phone_number: phoneNumber,
      });

      const response = await axios.post(
        "/api/login/initiate/",
        {
          phone_number: phoneNumber,
          country: country,
        },
        {
          withCredentials: true,
        }
      );

      if (response.data.verification_uuid) {
        setVerificationUuid(response.data.verification_uuid);
        setStep(1);
        setCanResend(false);
        setResendTimer(30);
        posthog.capture("login_verification_step_reached", {
          country: country,
        });
      }
    } catch (error) {
      if (error.response?.data?.error) {
        setError(error.response.data.error);
      } else {
        setError("Error initiating login. Please try again.");
      }
      Sentry.captureException(error, {
        extra: {
          phoneNumber,
          country,
        },
      });
    }
  };

  const handleVerifyLogin = async (code) => {
    setError("");

    try {
      const result = await dispatch(
        loginUser({
          phone_number: phoneNumber,
          country: country,
          verification_code: code,
          verification_uuid: verificationUuid,
        })
      ).unwrap();

      if (result.isAuthenticated) {
        posthog.capture("login_completed_successfully", {
          country: country,
        });
        postLoginRedirect();
      }
    } catch (error) {
      if (error?.error) {
        setError(error.error);
      } else {
        setError("An unexpected error occurred. Please try again later.");
      }
      Sentry.captureException(error, {
        extra: {
          phoneNumber,
          country,
        },
      });
    }
  };

  const handleResendCode = async () => {
    if (!canResend) return;
    setError("");
    try {
      posthog.capture("login_resend_code_clicked", {
        country: country,
      });

      const response = await axios.post(
        "/api/login/initiate/",
        {
          phone_number: phoneNumber,
          country: country,
        },
        {
          withCredentials: true,
        }
      );

      if (response.data.verification_uuid) {
        setVerificationUuid(response.data.verification_uuid);
        setCanResend(false);
        setResendTimer(30);
      }
    } catch (error) {
      setError("Error resending verification code. Please try again.");
      Sentry.captureException(error, {
        extra: {
          phoneNumber,
          country,
        },
      });
    }
  };

  const postLoginRedirect = () => {
    if (
      document.activeElement &&
      typeof document.activeElement.blur === "function"
    ) {
      document.activeElement.blur();
    }

    // Reset the scroll position
    window.scrollTo(0, 0);

    const searchParams = new URLSearchParams(location.search);
    const redirectUrl = searchParams.get("redirect");
    if (redirectUrl) {
      navigate(decodeURIComponent(redirectUrl));
    } else {
      navigate("/");
    }
  };

  const handleVerificationCodeChange = (value) => {
    // If we have already processed this value, skip
    if (value === lastProcessedValue.current) {
      return;
    }
    lastProcessedValue.current = value;

    // Always set the verification code
    setVerificationCode(value);
  };

  const handleCountryChange = (country) => {
    setCountry(country);
  };

  const renderStep = () => {
    // Check for the specific error message about country not supported
    if (error && error.includes(COUNTRY_BLOCKED_ERROR_MESSAGE)) {
      return (
        <Stack spacing={3} alignItems="center">
          <Typography
            sx={{
              textAlign: "center",
              color: "#F53D3B",
              fontSize: { xs: "18px", md: "28px" },
              fontWeight: 500,
              mb: 1,
            }}
          >
            Download our app
          </Typography>

          <Typography
            sx={{
              textAlign: "center",
              color: "#FFFFFF",
              mb: 2,
            }}
          >
            {error}
          </Typography>

          <AppStoreButton />
        </Stack>
      );
    }

    switch (step) {
      case 0:
        return (
          <Stack spacing={3}>
            <Box>
              <Typography
                sx={{
                  textAlign: { xs: "center", md: "left" },
                  color: "#F53D3B",
                  fontSize: { xs: "18px", md: "28px" },
                  fontWeight: 500,
                  mb: 1,
                }}
              >
                Staff Sign in
              </Typography>
            </Box>

            {/* Google Sign-In Button */}
            <Box sx={{ 
              width: "100%",
              "& div[role='button']": {
                justifyContent: "center !important"
              },
              "& div[role='button'] span": {
                fontSize: "14px !important",
                fontWeight: "500 !important",
                fontFamily: "Unbounded !important",
                textAlign: "center !important"
              },
              "& button": {
                justifyContent: "center !important"
              },
              "& button span": {
                fontSize: "14px !important",
                fontWeight: "500 !important",
                fontFamily: "Unbounded !important",
                textAlign: "center !important"
              }
            }}>
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                text="continue_with"
                theme="outline"
                size="large"
                width="100%"
                logo_alignment="left"
              />
            </Box>

            <Divider sx={{ color: "#8E8E93" }}>OR</Divider>

            <form onSubmit={handleInitiateLogin}>
              <PhoneNumberInput
                value={phoneNumber}
                onChange={setPhoneNumber}
                country={country}
                onCountryChange={handleCountryChange}
              />
              <PrimaryButton
                fullWidth
                type="submit"
                sx={{
                  mt: 2,
                  backgroundColor: "#FF3B30",
                  color: "#FFFFFF",
                  "&:hover": {
                    backgroundColor: "#FF453A",
                  },
                  "&:disabled": {
                    backgroundColor: "#3A3A3C",
                    color: "#8E8E93",
                  },
                }}
              >
                Continue
              </PrimaryButton>
            </form>
          </Stack>
        );
      case 1:
        return (
          <>
            <Typography
              sx={{
                textAlign: { xs: "center", md: "left" },
                color: "#F53D3B",
                fontSize: { xs: "18px", md: "28px" },
                fontWeight: 500,
                mb: 1,
              }}
            >
              Verification
            </Typography>
            <Typography sx={{ mb: 2, fontSize: "14px", color: "#9F9F9F" }}>
              Enter verification code
            </Typography>
            <form onSubmit={(e) => e.preventDefault()}>
              <MuiOtpInput
                value={verificationCode}
                onChange={handleVerificationCodeChange}
                length={6}
                validateChar={(value) => /^\d+$/.test(value)}
                TextFieldsProps={{
                  type: "tel",
                  placeholder: "",
                  size: "small",
                  sx: {
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#2C2C2E",
                      color: "#FFFFFF",
                      "&:hover": {
                        backgroundColor: "#3A3A3C",
                      },
                      "&.Mui-focused": {
                        backgroundColor: "#3A3A3C",
                      },
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#38383A",
                    },
                  },
                  inputProps: (index) => ({
                    type: "tel",
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                    maxLength: index === 0 ? 6 : 1,
                    style: {
                      textAlign: "center",
                      color: "#FFFFFF",
                    },
                    autoComplete: "one-time-code",
                    textContentType: "oneTimeCode",
                  }),
                }}
                sx={{
                  gap: 1,
                  "& .MuiOutlinedInput-input": {
                    px: 0,
                  },
                }}
              />
              <Typography
                gutterBottom
                sx={{ mt: 2, fontSize: "12px", color: "#9F9F9F" }}
              >
                If an account exists with this number, a verification code has
                been sent.
              </Typography>
              <Button
                variant="text"
                color="primary"
                fullWidth
                sx={{
                  mt: 1,
                  color: "#F53D3B",
                  width: "auto",
                  justifyContent: "flex-start",
                  "&:hover": {
                    color: "#FF453A",
                  },
                  "&.Mui-disabled": {
                    color: "rgba(245, 61, 56, 0.5)",
                  },
                }}
                onClick={handleResendCode}
                disabled={!canResend}
              >
                {canResend ? "Resend Code" : `Resend Code (${resendTimer}s)`}
              </Button>
              <Button
                variant="text"
                startIcon={<ArrowBackIcon />}
                onClick={() => setStep(0)}
                sx={{
                  mt: 1,
                  color: "white",
                  margin: "0 auto",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                Change phone number
              </Button>
            </form>
          </>
        );
      default:
        return null;
    }
  };

  return (
          <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          zIndex: 1300,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          pointerEvents: "none",
        }}
      >
      <MockBubbles />
      {isMobile ? (
        <SwipeableDrawer
          anchor="bottom"
          open={open}
          onClose={() => {
            setOpen(false);
            setIsCollapsed(true);
          }}
          variant="permanent"
          ModalProps={{
            keepMounted: true,
            BackdropProps: {
              sx: { backgroundColor: "transparent" },
            },
          }}
          PaperProps={{
            sx: {
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              height: "auto",
              maxHeight: "95vh",
              borderTopLeftRadius: 24,
              borderTopRightRadius: 24,
              backgroundColor: "#1C1C1E",
              color: "#FFFFFF",
              pointerEvents: "auto",
              transform: "none !important",
            },
          }}
        >
          <StyledBox sx={{ backgroundColor: "#1C1C1E", color: "#FFFFFF" }}>
            <Box sx={{ p: 2, pb: 2 }}>
              <CardContent sx={{ width: "100%", pt: 0 }}>
                <CSRFToken />
                {renderStep()}
                {error && !error.includes(COUNTRY_BLOCKED_ERROR_MESSAGE) && (
                  <Typography
                    color="error"
                    align="center"
                    sx={{ fontSize: "12px" }}
                  >
                    {error}
                    <br />
                    <Box component="span" sx={{ fontSize: "12px" }}>
                      Contact{" "}
                      <Link href="mailto:support@smoothspeak.ai">
                        support@smoothspeak.ai
                      </Link>{" "}
                      if you continue to face issues
                    </Box>
                  </Typography>
                )}
              </CardContent>
            </Box>
          </StyledBox>
        </SwipeableDrawer>
      ) : (
        <Container
          maxWidth="sm"
          sx={{
            position: "fixed",
            top: "15vh",
            left: "50%",
            transform: "translateX(-50%)",
            py: { xs: 0, md: 2 },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            pointerEvents: "auto",
          }}
        >
          <Box
            sx={{
              p: 4,
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              backgroundColor: "#1C1C1E",
              color: "#FFFFFF",
              borderRadius: 4,
              boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.5)",
            }}
          >
            <CardContent sx={{ width: "100%" }}>
              <CSRFToken />
              {renderStep()}
              {error && !error.includes(COUNTRY_BLOCKED_ERROR_MESSAGE) && (
                <Typography color="error" align="center">
                  {error}
                  <br />
                  <Box component="span">
                    Contact{" "}
                    <Link href="mailto:support@smoothspeak.ai">
                      support@smoothspeak.ai
                    </Link>{" "}
                    if you continue to face issues
                  </Box>
                </Typography>
              )}
            </CardContent>
          </Box>
        </Container>
      )}
    </Box>
  );
}

export default StaffLogin;
